import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Paper, styled, Typography } from "@mui/material";
import { ChildCategoryNavigationCarousel } from "~/feature/CategoryNavigation/v2/ChildCategoryNavigationCarousel";
import type { RootState } from "~/redux/store";
import type { NavigationNode } from "~/Header/types";
import type { NodeResponse } from "~/feature/CategoryNavigation/v2/ChildCategoryNavigation";
import { C_SURFACE_BRIGHT } from "~/common/components/icon/Colors";

const Root = styled(Paper)(({ theme }) => ({
  background: C_SURFACE_BRIGHT,
  padding: "16px",
  [theme.breakpoints.up("desktop-m")]: {
    padding: "24px",
  },
}));

interface TreeNode extends NavigationNode {
  childNodes: TreeNode[];
}

export const L1CategoryNavigation = () => {
  const { t } = useTranslation();
  // TODO: Awful code, revise
  const isAnyCategorySelected = useSelector((state: RootState) => state.srp.facets.selectedProductGroups.length > 0);
  const navigationNodes = useSelector((state: RootState) => state.navigation.categoryNavigation.nodes);
  const rootNode = useSelector((state: RootState) => {
    return Object.values(state.navigation.categoryNavigation.nodes).find(
      (node) => node.level === 0 && node.gaLabel?.toLowerCase() === "products",
    );
  });

  const categoryData: NodeResponse | null = useMemo(() => {
    if (!rootNode || !navigationNodes) {
      return null;
    }

    const buildNode = (node: NavigationNode): TreeNode => {
      return {
        ...node,
        childNodes: node.children
          ? node.children
              .map((childId) => navigationNodes[childId])
              .filter((childNode) => childNode !== undefined)
              .map((childNode) => buildNode(childNode))
          : [],
      };
    };

    const childNodes = rootNode.children
      .map((id) => navigationNodes[id])
      .filter((node) => node !== undefined)
      .map((node) => buildNode(node));

    return {
      parentNode: null,
      childNodes,
    };
  }, [rootNode, navigationNodes]);

  if (isAnyCategorySelected || !categoryData || categoryData.childNodes.length === 0) {
    return null;
  }

  return (
    <Root>
      <Typography variant="titleLarge" sx={{ mb: 2 }}>
        {t("page.srp.selectCategory.title", "Select a product group")}
      </Typography>
      <ChildCategoryNavigationCarousel category={categoryData} />
    </Root>
  );
};
